import { FOODS, type FoodId } from "./foods";
import type { Lane } from "../constants";

export type HazardKind =
  | "wolf"
  | "rock-small"
  | "rock-large"
  | "log"
  | "stump"
  | "branch"
  | "ravine"
  | "thorns";

export type EncounterKind = FoodId | HazardKind;

export type AvoidAction = "jump" | "duck" | "lane";

export type RunBandName = "tutorial" | "normal" | "intense" | "finale";

export interface PatternItem {
  delayMs: number;
  lane: Lane;
  kind: EncounterKind;
  wolfFrom?: Lane;
  wolfTo?: Lane;
}

export interface EncounterPattern {
  id: string;
  band: RunBandName;
  items: PatternItem[];
}

export function isFoodKind(kind: EncounterKind): kind is FoodId {
  return kind in FOODS;
}

export function isHazardKind(kind: EncounterKind): kind is HazardKind {
  return !isFoodKind(kind);
}

export function requiredAvoidAction(kind: EncounterKind): AvoidAction | null {
  if (isFoodKind(kind)) {
    return null;
  }
  if (kind === "branch") {
    return "duck";
  }
  return "jump";
}

export const ENCOUNTER_PATTERNS: EncounterPattern[] = [
  {
    id: "food-line-center",
    band: "tutorial",
    items: [
      { delayMs: 0, lane: 0, kind: "berries" },
      { delayMs: 320, lane: 0, kind: "berries" },
      { delayMs: 640, lane: 0, kind: "apple" },
    ],
  },
  {
    id: "food-line-left",
    band: "tutorial",
    items: [
      { delayMs: 0, lane: -1, kind: "berries" },
      { delayMs: 300, lane: -1, kind: "apple" },
      { delayMs: 600, lane: -1, kind: "acorns" },
    ],
  },
  {
    id: "food-weave-easy",
    band: "tutorial",
    items: [
      { delayMs: 0, lane: -1, kind: "berries" },
      { delayMs: 380, lane: 0, kind: "apple" },
      { delayMs: 760, lane: 1, kind: "berries" },
    ],
  },
  {
    id: "single-rock-center",
    band: "tutorial",
    items: [{ delayMs: 0, lane: 0, kind: "rock-small" }],
  },
  {
    id: "single-branch-right",
    band: "tutorial",
    items: [{ delayMs: 0, lane: 1, kind: "branch" }],
  },
  {
    id: "single-log-left",
    band: "tutorial",
    items: [
      { delayMs: 0, lane: -1, kind: "log" },
      { delayMs: 0, lane: 1, kind: "berries" },
    ],
  },
  {
    id: "wolf-right",
    band: "tutorial",
    items: [{ delayMs: 0, lane: 1, kind: "wolf", wolfFrom: 1, wolfTo: 1 }],
  },
  {
    id: "wolf-center-charge",
    band: "tutorial",
    items: [
      { delayMs: 0, lane: 0, kind: "wolf", wolfFrom: 0, wolfTo: 0 },
      { delayMs: 0, lane: -1, kind: "berries" },
    ],
  },
  {
    id: "ravine-center",
    band: "tutorial",
    items: [{ delayMs: 0, lane: 0, kind: "ravine" }],
  },
  {
    id: "stump-and-acorns",
    band: "normal",
    items: [
      { delayMs: 0, lane: 0, kind: "stump" },
      { delayMs: 0, lane: -1, kind: "acorns" },
    ],
  },
  {
    id: "branch-center-food-sides",
    band: "normal",
    items: [
      { delayMs: 0, lane: 0, kind: "branch" },
      { delayMs: 0, lane: -1, kind: "apple" },
      { delayMs: 0, lane: 1, kind: "apple" },
    ],
  },
  {
    id: "rocks-sides-center-open",
    band: "normal",
    items: [
      { delayMs: 0, lane: -1, kind: "rock-small" },
      { delayMs: 0, lane: 1, kind: "rock-large" },
      { delayMs: 200, lane: 0, kind: "honey" },
    ],
  },
  {
    id: "wolf-center-lane-out",
    band: "normal",
    items: [
      { delayMs: 0, lane: 0, kind: "wolf", wolfFrom: 0, wolfTo: 0 },
      { delayMs: 0, lane: 1, kind: "berries" },
    ],
  },
  {
    id: "log-then-branch",
    band: "normal",
    items: [
      { delayMs: 0, lane: 0, kind: "log" },
      { delayMs: 700, lane: 1, kind: "branch" },
      { delayMs: 700, lane: -1, kind: "acorns" },
    ],
  },
  {
    id: "ravine-left-food-right",
    band: "normal",
    items: [
      { delayMs: 0, lane: -1, kind: "ravine" },
      { delayMs: 0, lane: 1, kind: "apple" },
    ],
  },
  {
    id: "rock-center-weave",
    band: "normal",
    items: [
      { delayMs: 0, lane: -1, kind: "berries" },
      { delayMs: 280, lane: 0, kind: "rock-small" },
      { delayMs: 560, lane: 1, kind: "apple" },
    ],
  },
  {
    id: "thorns-left-open-right",
    band: "normal",
    items: [
      { delayMs: 0, lane: -1, kind: "thorns" },
      { delayMs: 0, lane: 1, kind: "acorns" },
    ],
  },
  {
    id: "wolf-cross-to-center",
    band: "normal",
    items: [
      { delayMs: 0, lane: -1, kind: "wolf", wolfFrom: -1, wolfTo: 0 },
      { delayMs: 0, lane: 1, kind: "honey" },
    ],
  },
  {
    id: "honey-beside-wolf",
    band: "intense",
    items: [
      { delayMs: 0, lane: 0, kind: "wolf", wolfFrom: 0, wolfTo: 0 },
      { delayMs: 0, lane: 1, kind: "honey" },
      { delayMs: 0, lane: -1, kind: "rock-small" },
    ],
  },
  {
    id: "wolf-cross-left-charge",
    band: "intense",
    items: [
      { delayMs: 0, lane: 1, kind: "wolf", wolfFrom: 1, wolfTo: -1 },
      { delayMs: 0, lane: 0, kind: "acorns" },
    ],
  },
  {
    id: "fish-risk-log",
    band: "intense",
    items: [
      { delayMs: 0, lane: 0, kind: "log" },
      { delayMs: 80, lane: 0, kind: "fish" },
      { delayMs: 0, lane: 1, kind: "berries" },
    ],
  },
  {
    id: "double-ravine-sides",
    band: "intense",
    items: [
      { delayMs: 0, lane: -1, kind: "ravine" },
      { delayMs: 0, lane: 1, kind: "ravine" },
      { delayMs: 0, lane: 0, kind: "acorns" },
    ],
  },
  {
    id: "branch-left-rock-right",
    band: "intense",
    items: [
      { delayMs: 0, lane: -1, kind: "branch" },
      { delayMs: 0, lane: 1, kind: "rock-large" },
      { delayMs: 350, lane: 0, kind: "honey" },
    ],
  },
  {
    id: "wolf-cross-right-fish",
    band: "intense",
    items: [
      { delayMs: 0, lane: -1, kind: "wolf", wolfFrom: -1, wolfTo: 1 },
      { delayMs: 0, lane: 0, kind: "fish" },
    ],
  },
  {
    id: "stump-branch-combo",
    band: "intense",
    items: [
      { delayMs: 0, lane: 0, kind: "stump" },
      { delayMs: 620, lane: 0, kind: "branch" },
      { delayMs: 620, lane: 1, kind: "apple" },
    ],
  },
  {
    id: "thorns-and-rock-squeeze",
    band: "intense",
    items: [
      { delayMs: 0, lane: -1, kind: "thorns" },
      { delayMs: 0, lane: 1, kind: "rock-small" },
      { delayMs: 200, lane: 0, kind: "honey" },
    ],
  },
  {
    id: "log-sides-jump-center",
    band: "intense",
    items: [
      { delayMs: 0, lane: -1, kind: "log" },
      { delayMs: 0, lane: 1, kind: "rock-small" },
      { delayMs: 0, lane: 0, kind: "fish" },
    ],
  },
  {
    id: "finale-food-fan",
    band: "finale",
    items: [
      { delayMs: 0, lane: -1, kind: "apple" },
      { delayMs: 0, lane: 0, kind: "honey" },
      { delayMs: 0, lane: 1, kind: "apple" },
      { delayMs: 420, lane: 0, kind: "acorns" },
    ],
  },
  {
    id: "finale-easy-gap",
    band: "finale",
    items: [
      { delayMs: 0, lane: 0, kind: "rock-small" },
      { delayMs: 0, lane: -1, kind: "berries" },
      { delayMs: 0, lane: 1, kind: "berries" },
    ],
  },
  {
    id: "finale-honey-line",
    band: "finale",
    items: [
      { delayMs: 0, lane: 0, kind: "honey" },
      { delayMs: 300, lane: 0, kind: "fish" },
    ],
  },
];

export function patternsForBand(band: RunBandName): EncounterPattern[] {
  return ENCOUNTER_PATTERNS.filter((pattern) => pattern.band === band);
}
